/*
 * Books' front page, like QuickBooks' and Xero's dashboards: money in the bank now, this month's profit next to last
 * month's, income and costs by month, the biggest costs, each account's balance and reconcile, and what still needs the
 * owner (getting started, transactions with no category, accounts not reconciled, W-9s, links that stopped).
 */
import { monthEnd, spanOf, type BooksSetup } from "@steward/shared";
import { use1099, useBalance, usePnl } from "../../api/books";
import { useInvoices } from "../../api/money";
import { useSession } from "../../app/session";
import { BarChart, Change, Legend } from "../../components/Charts";
import { money, monthName, plural, shortDate } from "../../components/format";
import { Button, Card, Skeleton, StatusLabel } from "../../components/parts";
import { Icon } from "../../components/Icon";
import { KIND_WORDS, Tile, whole } from "./common";

export type GoTo = "transactions" | "review" | "accounts" | "reconcile" | "reports" | "c1099" | "yearend";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function Overview({ setup, today, go }: { setup: BooksSetup; today: string; go: (to: GoTo) => void }) {
  const { company, can } = useSession();
  const year = Number(today.slice(0, 4));
  const yearSpan = spanOf("year", today);
  const pnl = usePnl(company.id, yearSpan.from, yearSpan.to);
  const last = spanOf("lastmonth", today);
  const lastPnl = usePnl(company.id, last.from, last.to);
  const balance = useBalance(company.id, today);
  const c1099 = use1099(company.id, year);
  const owed = useInvoices(company.id, { state: "all" });
  const p = pnl.data;
  const b = balance.data;
  const month = Number(today.slice(5, 7));
  const income = p
    ? MONTHS.map((_, i) => p.rows.filter((r) => r.type === "income").reduce((a, r) => a + (r.byMonth[i] ?? 0), 0))
    : [];
  const costs = p
    ? MONTHS.map((_, i) => p.rows.filter((r) => r.type === "expense").reduce((a, r) => a + (r.byMonth[i] ?? 0), 0))
    : [];
  const profitNow = (income[month - 1] ?? 0) - (costs[month - 1] ?? 0);
  const profitBefore = lastPnl.data ? lastPnl.data.profitCents : 0;
  const topCosts = (p?.rows ?? [])
    .filter((r) => r.type === "expense" && r.totalCents > 0)
    .sort((a, c) => c.totalCents - a.totalCents)
    .slice(0, 6);
  const maxCost = topCosts[0]?.totalCents ?? 1;

  // What still needs the owner.
  const lastMonthEnd = monthEnd(last.from.slice(0, 7));
  const bankish = setup.accounts.filter((a) => a.kind !== "cash");
  const notReconciled = bankish.filter((a) => !a.reconciledThrough || a.reconciledThrough < lastMonthEnd);
  const noOpening = bankish.filter((a) => !a.openingDay);
  const noW9 = (c1099.data?.payees ?? []).filter((x) => x.reaches && !x.w9);
  const open = p?.uncategorizedCount ?? 0;
  const steps = [
    { done: setup.accounts.length > 0, title: "Add your bank and card accounts", to: "accounts" as const },
    { done: bankish.length > 0 && noOpening.length === 0, title: "Set each one's opening balance", to: "accounts" as const },
    {
      done: setup.years.length > 0,
      title: "Bring in transactions (link the bank or upload its file)",
      to: "transactions" as const,
    },
    { done: setup.rules.length > 0, title: "Make a rule for a cost you pay every month", to: "review" as const },
    {
      done: bankish.some((a) => a.reconciledThrough),
      title: "Reconcile an account to its statement",
      to: "reconcile" as const,
    },
  ];
  const started = steps.every((s) => s.done);
  const todo: {
    icon: "warning" | "books" | "checklist" | "file" | "invoices";
    text: string;
    action: string;
    to: GoTo | "invoices";
  }[] = [];
  if (open)
    todo.push({
      icon: "books",
      text: `${plural(open, "transaction")} ${open === 1 ? "needs" : "need"} a category`,
      action: "Review",
      to: "review",
    });
  if (bankish.length && notReconciled.length)
    todo.push({
      icon: "checklist",
      text: `${plural(notReconciled.length, "account")} not reconciled through ${monthName(last.from)}`,
      action: "Reconcile",
      to: "reconcile",
    });
  if (noW9.length)
    todo.push({
      icon: "file",
      text: `${plural(noW9.length, "contractor")} past the 1099 line with no W-9 on file`,
      action: "See 1099s",
      to: "c1099",
    });
  if (setup.lockDay === null && month >= 4)
    todo.push({
      icon: "file",
      text: `Close ${String(year - 1)} once your taxes are filed, so it cannot change`,
      action: "Year end",
      to: "yearend",
    });

  return (
    <div className="stack">
      {!started ? (
        <Card title="Get Books ready">
          <ol className="bk-steps">
            {steps.map((s, i) => (
              <li key={s.title} className={s.done ? "done" : undefined}>
                <span className="bk-step-mark" aria-hidden="true">
                  {s.done ? <Icon name="check" size={14} /> : i + 1}
                </span>
                <span className="bk-step-text">{s.title}</span>
                {!s.done ? (
                  <Button size="small" type="secondary" onClick={() => go(s.to)}>
                    Start
                  </Button>
                ) : (
                  <span className="sr-only">Done</span>
                )}
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      <div className="tiles">
        <Tile
          label="In the bank now"
          value={b ? whole(b.cashCents) : "..."}
          hint={b?.cardsOwedCents ? `Cards owe ${money(b.cardsOwedCents)}` : "Banks and cash"}
        />
        <div className="tile">
          <span className="label">Profit in {monthName(today)}</span>
          <span className="value">{p ? whole(profitNow) : "..."}</span>
          {lastPnl.data ? (
            <span className="hint">
              <Change now={profitNow} before={profitBefore} /> next to {monthName(last.from)}
            </span>
          ) : null}
        </div>
        <Tile label={`Income in ${String(year)}`} value={p ? whole(p.incomeCents) : "..."} hint="So far this year" />
        <Tile
          label={`Profit in ${String(year)}`}
          value={p ? whole(p.profitCents) : "..."}
          hint={
            p?.incomeCents
              ? `${String(Math.round((p.profitCents / p.incomeCents) * 100))} cents of each dollar`
              : "Income less costs"
          }
        />
        {can("invoice.read") && owed.data ? (
          <Tile
            label="Clients owe"
            value={whole(owed.data.summary.owedCents)}
            hint={owed.data.summary.pastDueCents ? `${money(owed.data.summary.pastDueCents)} past due` : "None past due"}
          />
        ) : null}
      </div>

      {todo.length || setup.connections.some((c) => c.status !== "ok") ? (
        <Card title="Needs you" flush>
          <ul className="bk-todo">
            {setup.connections
              .filter((c) => c.status !== "ok")
              .map((c) => (
                <li key={c.id}>
                  <Icon name="warning" size={18} />
                  <span>{c.institution ?? "A linked bank"} stopped sending transactions</span>
                  <Button size="small" type="secondary" onClick={() => go("transactions")}>
                    Fix
                  </Button>
                </li>
              ))}
            {todo.map((t) => (
              <li key={t.text}>
                <Icon name={t.icon} size={18} />
                <span>{t.text}</span>
                <Button size="small" type="secondary" onClick={() => (t.to === "invoices" ? undefined : go(t.to))}>
                  {t.action}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="grid-2 bk-overview">
        <Card title={`Income and costs, ${String(year)}`}>
          {!p ? (
            <Skeleton rows={5} />
          ) : (
            <>
              <Legend
                items={[
                  { name: "Income", tone: "main", value: money(p.incomeCents) },
                  { name: "Costs", tone: "soft", value: money(p.expenseCents) },
                ]}
              />
              <BarChart
                labels={MONTHS}
                series={[
                  { name: "Income", values: income.map((v, i) => (i < month ? v / 100 : 0)), tone: "main" },
                  { name: "Costs", values: costs.map((v, i) => (i < month ? v / 100 : 0)), tone: "soft" },
                ]}
                format={(v) => money(Math.round(v * 100))}
                axisPrefix="$"
                height={220}
              />
            </>
          )}
        </Card>
        <Card
          title="Biggest costs this year"
          action={
            <Button size="small" type="tertiary" onClick={() => go("reports")}>
              Profit and loss
            </Button>
          }
        >
          {!p ? (
            <Skeleton rows={5} />
          ) : topCosts.length === 0 ? (
            <p className="muted">No costs with a category yet.</p>
          ) : (
            <ul className="bk-bars">
              {topCosts.map((r) => (
                <li key={r.categoryId}>
                  <span className="bk-bar-name">{r.name}</span>
                  <span className="bk-bar-track" aria-hidden="true">
                    <span style={{ width: `${String(Math.max(2, (r.totalCents / maxCost) * 100))}%` }} />
                  </span>
                  <b className="num">{money(r.totalCents)}</b>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card
        title="Accounts"
        flush
        action={
          <Button size="small" type="tertiary" onClick={() => go("accounts")}>
            Manage
          </Button>
        }
      >
        {setup.accounts.length === 0 ? (
          <p className="muted bk-pad">No accounts yet.</p>
        ) : (
          <ul className="bk-accounts">
            {setup.accounts.map((a) => {
              const bal = b?.accounts.find((x) => x.id === a.id)?.balanceCents;
              const behind = a.kind !== "cash" && (!a.reconciledThrough || a.reconciledThrough < lastMonthEnd);
              return (
                <li key={a.id}>
                  <span className="bk-acct-icon" aria-hidden="true">
                    <Icon name={a.kind === "card" ? "payments" : a.kind === "cash" ? "money" : "building"} size={18} />
                  </span>
                  <span className="bk-acct-name">
                    <b>{a.name}</b>
                    <span className="muted small">
                      {KIND_WORDS[a.kind]}
                      {a.last4 ? ` ending ${a.last4}` : ""}
                      {a.linked ? ", linked" : ""}
                    </span>
                  </span>
                  <span className="bk-acct-rec">
                    {a.kind === "cash" ? null : a.reconciledThrough ? (
                      <StatusLabel status={behind ? "warning" : "success"}>
                        Reconciled to {shortDate(a.reconciledThrough, year)}
                      </StatusLabel>
                    ) : (
                      <StatusLabel status="inactive">Not reconciled</StatusLabel>
                    )}
                  </span>
                  <b className="num bk-acct-bal">
                    {bal === undefined ? (
                      ""
                    ) : bal === null ? (
                      <span className="muted small">Set opening balance</span>
                    ) : (
                      money(bal)
                    )}
                  </b>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
